import { Component, effect, inject, input } from '@angular/core';

import { EChartsOption, LegendComponentOption, SeriesOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { RegisterComponent } from '../../components/component-lookup';
import { VisualBaseComponent } from '../visual-base.component';
import { DateRange, Peer, StateService, SubjectType } from '../../services/state.service';
import { DateService } from '../../services/date.service';
import { GlobalConstants } from '../../../shared/global-constants';
import { VisualService } from '../../services/visual.service';
import { ChartInfoBoxComponent } from '../chart-info-box/chart-info-box.component';
import { ElementMetricMax } from '../../services/metric.service';

@RegisterComponent('BarLineClusteredChart')
@Component({
  selector: 'app-bar-line-clustered-chart',
  standalone: true,
  imports: [NgxEchartsDirective, ChartInfoBoxComponent],
  templateUrl: './bar-line-clustered-chart.component.html',
  styleUrl: './bar-line-clustered-chart.component.scss',
  providers: [provideEcharts()]
})
export class BarLineClusteredChartComponent extends VisualBaseComponent {
  elementId = input.required<string>();
  subjectType = input.required<SubjectType>();
  yearEndOnly = input<boolean>(false);

  private dateService = inject(DateService);

  readonly institution = inject(StateService).institution;
  readonly peer = inject(StateService).peer;
  readonly filteredGroup = inject(StateService).filteredGroup;
  readonly dateRange = inject(StateService).dateRange;

  override loadingColor = '#ebebeb';
  override initOptions = { height: 250 }
  override baseOptions: EChartsOption = {
    grid: {
      left: 10,
      right: 10,
      top: 10,
      bottom: 35,
      containLabel: true
    },
    title: {
      show: false
    },
    tooltip: {
      show: true,
      trigger: 'axis'
    },
    legend: {
      selectedMode: true
    },
    xAxis: {
      type: 'category',
      show: true,
      axisLabel: {
        color: '#243b5b'
      }
    },
    yAxis: [
      {
        type: 'value',
        position: 'left',
        show: true,
        splitNumber: 4,
        splitLine: {
          lineStyle: {
            color: '#ccc'
          }
        },
        axisLabel: {
          color: '#243b5b'
        }
      },
      {
        type: 'value',
        position: 'right',
        show: true,
        splitNumber: 4,
        splitLine: {
          show: false
        },
        axisLabel: {
          color: '#243b5b'
        }
      }
    ],
    axisLabel: {
      color: '#002f8e'
    }
  };

  constructor() {
    super();

    effect(() => {
      const subjectType = this.subjectType();
      const peer = subjectType == SubjectType.peer ? this.peer() : undefined;
      const dateRange = this.dateRange();
      const elementId = this.elementId();
      const group = this.filteredGroup();
      if (this.chartInit() && subjectType && (peer || subjectType == SubjectType.institution) && dateRange && elementId && group) {
        this.updateVisual(peer, dateRange, elementId);
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  override onChartLegendSelectChanged(event: any) {
    const options: EChartsOption = {
      yAxis: {
        max: 100,
        minInterval: 25,
        maxInterval: 25
      }
    };
    this.updateOptions = options;
  }

  public updateVisual(peer: Peer | undefined, dateRange: DateRange, elementId: string) {
    this.metricService.getElementData(this.institution().iid, elementId, peer).subscribe(metrics => {
      this.metricService.getElementDataMax(this.institution().iid, elementId, this.peer()).subscribe(maxMetrics => {
        const dateSeries = this.dateService.getDateSeries(this.institution().institutionType, dateRange, this.yearEndOnly());
        const maxValue = this.getMaxValue(maxMetrics, dateSeries.dates);

        const displayTypes: { seriesName: string, displayType: string }[] = [];

        let barIndex = 0;
        const barSeries: SeriesOption[] = [];
        const barLegendData: string[] = [];

        let lineIndex = 0;
        const lineSeries: SeriesOption[] = [];
        const lineLegendData: string[] = [];
        const lineDisplayType: string[] = [];
        const barDisplayType: string[] = [];

        const otherBarData: number[] = [];
        let hasMetricData = false;

        metrics.forEach(metric => {
          let indexes: number[] = [];
          if (this.subjectType() == SubjectType.peer) {
            indexes = this.visualService.getPeerIndexes(metric.data[0]);
          } else {
            indexes.push(metric.data[0].findIndex((c: number) => {
              return c == this.institution().iid;
            }));
          }

          const institutionIndex = metric.data[0].indexOf(this.institution().iid);

          if (metric.compType.toLowerCase() == 'bar') {
            barDisplayType.push(metric.displayType);
            const barData: (number | null)[] = [];
            metric.data.forEach(data => {
              const dataRow = dateSeries.dates.findIndex(d => { return new Date(d).getTime() == new Date(data[0]).getTime() });

              let sum: number | null = null; let count = 0;
              indexes.forEach(i => {
                if (dataRow > -1 && data[i] != null) {
                  sum += data[i]; count++;
                }
              });
              const value: number | null = (sum != null && count > 0) ? sum / count : null;

              if (dataRow > -1 && value != null) {
                if (metric.rowTitle != 'Other') {
                  barData[dataRow] = VisualService.getValue(value, metric.displayType);
                }
                else {
                  const current = otherBarData[dataRow] ?? 0;
                  otherBarData[dataRow] = current + (VisualService.getValue(value, metric.displayType) ?? 0);
                }
                hasMetricData = true;
              }
            });

            if (metric.rowTitle != 'Other') {
              barLegendData.push(metric.rowTitle);
              barSeries.push(
                {
                  type: 'bar',
                  name: metric.rowTitle,
                  data: barData,
                  color: this.chartColors().find(c => c.metricId == metric.metricId)?.color ?? GlobalConstants.barColors[barIndex++]
                }
              );
              displayTypes.push({ seriesName: metric.rowTitle, displayType: metric.displayType });
            }
          }

          if (metric.compType.toLowerCase() == 'line') {
            lineDisplayType.push(metric.displayType);
            const lineData: (number | null)[] = [];
            metric.data.forEach(data => {
              const dataRow = dateSeries.dates.findIndex(d => { return new Date(d).getTime() == new Date(data[0]).getTime() });

              let sum: number | null = null; let count = 0;
              indexes.forEach(i => {
                if (dataRow > -1 && data[i] != null) {
                  sum += data[i]; count++;
                }
              });
              let value: number | null = (sum != null && count > 0) ? sum / count : null;

              if (metric.dataType == 'rate') {
                value = data[institutionIndex != -1 ? institutionIndex : 1];
              }

              if (dataRow > -1 && value != null) {
                lineData[dataRow] = VisualService.getValue(value, metric.displayType);
                if (metric.dataType == 'metric') {
                  hasMetricData = true;
                }
              }
            });

            lineLegendData.push(metric.rowTitle);
            lineSeries.push(
              {
                type: 'line',
                lineStyle: {
                  type: metric.dataType == 'metric' ? 'solid' : 'dashed'
                },
                name: metric.rowTitle,
                symbol: 'line',
                symbolSize: 0,
                yAxisIndex: 1,
                data: lineData,
                color: this.chartColors()?.find(c => c.metricId == metric.metricId)?.color ?? GlobalConstants.lineColors[lineIndex++]
              }
            );
            displayTypes.push({ seriesName: metric.rowTitle, displayType: metric.displayType });
          }
        });

        if (otherBarData.length > 0) {
          barLegendData.push('Other');
          barSeries.push(
            {
              type: 'bar',
              name: 'Other',
              data: otherBarData,
              color: this.chartColors()?.find(c => c.metricId == 'Other')?.color ?? GlobalConstants.barColors[barIndex++]
            }
          );
          displayTypes.push({ seriesName: 'Other', displayType: metrics.find(m => m.compType.toLowerCase() == 'bar')?.displayType ?? '' });
        }

        const legend: LegendComponentOption[] = [];
        if (barSeries.length > 0) {
          legend.push({
            data: barLegendData,
            orient: 'vertical',
            left: 'right',
            top: barSeries.length > 8 ? 5 : 25,
            selectedMode: true
          });
        }

        if (lineSeries.length > 0) {
          legend.push({
            data: lineLegendData,
            orient: 'horizontal',
            top: 8,
            selectedMode: true
          });
        }

        if (!hasMetricData) {
          this.echartsInstance?.clear();
          this.chartOptions = this.noDataOptions;
        } else {
          this.updateOptions = {
            grid: {
              top: (lineSeries.length == 0) ? 20 : 40,
              bottom: 10,
              right: 175
            },
            legend: legend,
            tooltip: {
              formatter: function (params: { name: string, color: string; seriesName: string; seriesType: string, value: number; }[]) {
                let tooltip: string = '<div style="margin: 0px 0 0;line-height:1;"><div style="margin: 0px 0 0;line-height:1;"><div style="font-size:14px;color:#666;font-weight:400;line-height:1;">' + params[0].name + '</div><div style="margin: 10px 0 0;line-height:1;">';
                params.forEach((param: { color: string; seriesName: string; seriesType: string, value: number; }) => {
                  if (param.value != undefined) {
                    const paramValue = VisualService.getValueDisplay(param.value, displayTypes.find(d => d.seriesName == param.seriesName)?.displayType ?? '');
                    tooltip += '<div style="margin: 10px 0 0;line-height:1;"><div style="margin: 0px 0 0;line-height:1;"><span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color: ' + param.color + ';"></span><span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">' + param.seriesName + '</span><span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">' + paramValue + '</span><div style="clear:both"></div></div><div style="clear:both"></div></div>'
                  }
                });
                tooltip += '</div></div></div>'
                return tooltip;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            },
            xAxis: {
              data: dateSeries.labels
            },
            yAxis: [
              {
                // min: 0,
                // max: maxValue * 1.1,
                axisLabel: {
                  formatter: function (value: number) {
                    // const decimals = (Number.isInteger(value) || value == 0) ? 1 : (value < .01) ? 3 : 2;
                    // return (value).toFixed(decimals) + '%';
                    return VisualService.getValueDisplay(value, barDisplayType[0]);
                  }
                }
              },
              {
                axisLabel: {
                  formatter: function (value: number) {
                    // const decimals = (Number.isInteger(value) || value == 0) ? 1 : value < .01 ? 3 : 2;
                    // return (value).toFixed(decimals) + '%';
                    return VisualService.getValueDisplay(value, lineDisplayType[0]);
                  }
                }
              }
            ],
            series: barSeries.concat(lineSeries)
          };
        }
        if (!this.echartsInstance?.isDisposed()) {
          this.echartsInstance?.hideLoading();
        }
      });
    });
  }

  private getMaxValue(metrics: ElementMetricMax[], dateSeries: Date[]): number {
    const values = metrics.filter(m => dateSeries.find(d => new Date(d).getTime() == new Date(m.reportDate).getTime()));
    return Math.max(...values.map(m => m.value ?? 0));
  }
}
